"use strict";

exports.__esModule = true;
exports.IndustryIcon = void 0;
var _VideoClicks = require("./VideoClicks");
var _StringUtils = require("../Utils/StringUtils");
var _CreativeEventHandler2 = require("../Public/CreativeEventHandler");
var _TrackingReport = require("./TrackingReport");
var _IconClickFallbackImage = require("./IconClickFallbackImage");
var _Resource = require("./Resource");
var _VASTProperty = require("./VASTProperty");
var _CreativeUtils = require("../Utils/CreativeUtils");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); } /*
 * COPYRIGHT 2020-2023 YOSPACE TECHNOLOGIES LTD. ALL RIGHTS RESERVED.
 * The contents of this file are proprietary and confidential.
 * Unauthorised copying of this file, via any medium is strictly prohibited.
 */ // JsDoc/Typescript imports
/**
 * Represents an industry icon object.
 *
 * Encapsulates the data from the `<Icon>` element of a VAST document.<br/>
 * It is a graphic that overlays a small portion of the advert's video and is sometimes clickable.
 *
 * <b>NOTE:</b> The class implements CreativeEventHandler so that it can raise `IconClickTracking` events on behalf of a client.
 *
 * @hideconstructor
 * @see {@link CreativeEventHandler}, which this class implements
 */
var IndustryIcon = exports.IndustryIcon = /*#__PURE__*/function (_CreativeEventHandler) {
  _inheritsLoose(IndustryIcon, _CreativeEventHandler);
  function IndustryIcon(properties, resources, videoClicks, clickFallbackImages, broker) {
    var _this;
    _this = _CreativeEventHandler.call(this) || this;

    /** @private */
    _this.resources = new Map();
    /** @private */
    _this.visible = false;
    /** @private */
    _this.properties = properties ? properties : [];
    /** @private */
    _this.videoClicks = videoClicks ? videoClicks : new _VideoClicks.VideoClicks();
    /** @private */
    _this.clickFallbackImages = clickFallbackImages ? clickFallbackImages : [];
    /** @private */
    _this.broker = broker;
    for (var _iterator = _createForOfIteratorHelperLoose(resources), _step; !(_step = _iterator()).done;) {
      var res = _step.value;
      _this.resources.set(res.getResourceType(), res);
    }
    return _this;
  }

  /**
   * Returns the click fallback images for the industry icon
   * @return {IconClickFallbackImage[]} an array of {@link IconClickFallbackImage} objects
   */
  var _proto = IndustryIcon.prototype;
  _proto.getClickFallbackImages = function getClickFallbackImages() {
    return Object.assign([], this.clickFallbackImages);
  };
  _proto.getClickThroughUrl = function getClickThroughUrl() {
    return this.videoClicks.getClickThroughUrl();
  };
  _proto.getClickTrackingUrls = function getClickTrackingUrls() {
    return this.videoClicks.getClickTrackingUrls();
  }

  /**
   * @return {boolean} true if the industry icon is visible
   */;
  _proto.isVisible = function isVisible() {
    return this.visible;
  };
  _proto.getViewTrackingUrls = function getViewTrackingUrls() {
    return this.videoClicks.getCustomClicks();
  }

  /**
   * Returns the properties for the industry icon. The properties are represented as VASTProperty objects. Possible properties are:
   * - program
   * - width
   * - height
   * - xPosition
   * - yPosition
   * - offset
   * - duration
   * - apiFramework
   * - altText
   * - hoverText
   *
   * @return {VASTProperty[]} an array of {@link VASTProperty} objects for the industry icon
   */;
  _proto.getProperties = function getProperties() {
    return Object.assign([], this.properties);
  }

  /**
   * Returns a named property for the industry icon if it exists. Possible properties are
   * - program
   * - width
   * - height
   * - xPosition
   * - yPosition
   * - offset
   * - duration
   * - apiFramework
   * - altText
   * - hoverText
   *
   * @param {string} name the name of the property
   * @return {VASTProperty} a {@link VASTProperty} object or null if not found
   */;
  _proto.getProperty = function getProperty(name) {
    for (var _iterator2 = _createForOfIteratorHelperLoose(this.properties), _step2; !(_step2 = _iterator2()).done;) {
      var prop = _step2.value;
      if (prop.getName() === name) {
        return prop;
      }
    }
  }

  /**
   * Returns the resource of the requested type.
   *
   * @param {ResourceType} type the resource type
   * @return {Resource} the {@link Resource} of the type specified, or null if not found
   */;
  _proto.getResource = function getResource(type) {
    return this.resources.get(type);
  }

  /**
   * Returns the resources for the industry icon. Each entry contains an array of resources keyed on the resource type.<br/>
   * The three resource types are optional elements; the dictionary will contain between one and three entries.
    * @return {Resource[]} an array of {@link Resource} objects for this industry icon
   */;
  _proto.getResources = function getResources() {
    return this.resources;
  };
  _proto.isValid = function isValid() {
    var program = null;
    var width = null;
    var height = null;
    var xPos = null;
    var yPos = null;
    for (var _iterator3 = _createForOfIteratorHelperLoose(this.properties), _step3; !(_step3 = _iterator3()).done;) {
      var prop = _step3.value;
      var value = prop.getValue();
      if (!(0, _StringUtils.isNullOrEmpty)(value)) {
        if (prop.getName() === "program") {
          program = prop;
        }
        if (prop.getName() === "width") {
          width = prop;
        }
        if (prop.getName() === "height") {
          height = prop;
        }
        if (prop.getName() === "xPosition") {
          xPos = prop;
        }
        if (prop.getName() === "yPosition") {
          yPos = prop;
        }
      }
    }
    return width !== null && height !== null && program !== null && xPos !== null && yPos !== null && this.resources.size > 0;
  };
  _proto.onClickThrough = function onClickThrough() {
    if (this.isVisible()) {
      var urls = this.videoClicks.getClickTrackingUrls();
      if (urls.length > 0) {
        var report = (0, _CreativeUtils.getClickTrackingReport)(urls, "IconClickTracking");
        if (report) {
          this.broker.fireTrackingReportForIcon(report, this);
        }
      }
    }
  };
  _proto.onTrackingEvent = function onTrackingEvent(_event) {
    // No op
  }

  /**
   * Defines whether the Industry Icon is being presented to the user.
   * @param {boolean} visible the visibility flag
   */;
  _proto.setVisible = function setVisible(visible) {
    if (this.visible !== visible) {
      this.visible = visible;
      if (this.visible) {
        if (this.videoClicks.getCustomClicks().length > 0) {
          var report = new _TrackingReport.TrackingReport("IconViewTracking", this.videoClicks.getCustomClicks());
          if (report) {
            this.broker.fireTrackingReportForIcon(report, this);
          }
        }
      }
    }
  };
  _proto.toString = function toString() {
    var s = "";
    s += this.videoClicks.toString();
    if (this.properties.length > 0) {
      s += "\nIcon Properties: ";
    }
    for (var _iterator4 = _createForOfIteratorHelperLoose(this.properties), _step4; !(_step4 = _iterator4()).done;) {
      var prop = _step4.value;
      s += "\n" + (0, _StringUtils.toIndentedString)(prop);
    }
    if (this.resources.length > 0) {
      s += "\nIcon Resource(s): ";
    }
    for (var _iterator5 = _createForOfIteratorHelperLoose(this.resources), _step5; !(_step5 = _iterator5()).done;) {
      var _step5$value = _step5.value,
        key = _step5$value[0],
        value = _step5$value[1];
      s += (0, _StringUtils.toIndentedString)(key + ":" + value);
    }
    if (this.clickFallbackImages.length > 0) {
      s += "\nIcon Click Fallback Images: ";
    }
    for (var _iterator6 = _createForOfIteratorHelperLoose(this.clickFallbackImages), _step6; !(_step6 = _iterator6()).done;) {
      var cfi = _step6.value;
      s += "\n" + (0, _StringUtils.toIndentedString)(cfi);
    }
    return s;
  };
  return IndustryIcon;
}(_CreativeEventHandler2.CreativeEventHandler);